import { useEffect, useRef } from 'react';
import { useU2000WebSocketContext } from '../../../../u2000Core/context/websocket/U2000WebSocketContext';
import {
  isNotificationMessage,
  U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF_JOB_TYPE,
} from '../../../../u2000Core/types/U2000WebSocketMessages';

export type U3141ImportChainAnnotationsNotificationPayload = {
  masterLotChainId: string;
  lotsAppliquesCount?: number;
  failed?: boolean;
  message?: string;
};

/**
 * Notifications WebSocket pour le job U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF (BL-0700).
 */
export function useU3141ImportChainAnnotationsNotification(
  masterLotChainId: string | undefined,
  onCompleted: (payload: U3141ImportChainAnnotationsNotificationPayload) => void,
) {
  const { on, off } = useU2000WebSocketContext();
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!masterLotChainId) return;

    const handleNotification = (message: unknown) => {
      if (!isNotificationMessage(message)) return;

      const metadata = message.metadata as Record<string, unknown> | undefined;
      const jobType = metadata?.jobType as string | undefined;
      const masterLotChainIdFromMsg = metadata?.masterLotChainId as string | undefined;

      if (
        jobType !== U3141_IMPORT_CHAIN_ANNOTATIONS_FROM_PDF_JOB_TYPE ||
        masterLotChainIdFromMsg !== masterLotChainId
      ) {
        return;
      }

      const appliedRaw = metadata?.lotsAppliquesCount;
      const lotsAppliquesCount = typeof appliedRaw === 'number' ? appliedRaw : undefined;

      onCompletedRef.current({
        masterLotChainId,
        lotsAppliquesCount,
        failed: message.importance === 'error' || message.importance === 'critical',
        message: message.message,
      });
    };

    on('notification:new', handleNotification);
    return () => off('notification:new', handleNotification);
  }, [masterLotChainId, on, off]);
}
